import { logger } from "@/logging";
import { getFrequencyBins } from "@/plugins/audio/audioRegistry";
import { getVideoRenderer, setVideoCanvas, setVideoSize } from "@/plugins/video/videoRegistry";
import { useRuntime } from "@/ui/contexts/RuntimeProvider";
import { useCallback, useMemo } from "react";
import type { ExposedEndpoint } from "@/graph/GraphDefinition";

/**
 * Hook that resolves the video renderer path and provides callbacks for visualization.
 *
 * Returns null if the node doesn't exist (e.g., during encapsulation when nodes move
 * between definitions).
 */
export function useVideoRenderer(instanceId: string, nodeId: string) {
  const { getValue, tweakValue, getPayload, definitionState, instanceState } = useRuntime();

  // Resolve node info - may be undefined during encapsulation
  const defId = instanceState[instanceId]?.definitionId;
  const node = defId ? definitionState[defId]?.nodes?.[nodeId] : undefined;
  const nodeType = node?.type;
  const vizNode = nodeType ? definitionState[nodeType]?.vizNode : undefined;

  /**
   * Resolve the actual renderer location in the video registry.
   *
   * For primitive nodes (e.g., PerspectiveCamera), the renderer is registered
   * directly at instanceId.nodeId (e.g., "root.camera_abc").
   *
   * For subgraph nodes with a vizNode set, the renderer lives inside the
   * subgraph at a different path. For example:
   * - Parent view: instanceId="root", nodeId="litScene_xyz"
   * - Subgraph's vizNode: "perspectiveCamera_abc"
   * - Actual renderer path: "root/litScene_xyz.perspectiveCamera_abc"
   */
  const resolvedPath = useMemo(() => {
    if (!node) return null;
    if (!vizNode) return { instanceId, nodeId };

    // Subgraph has a vizNode - resolve to the internal renderer path
    const internalInstanceId = `${instanceId}/${nodeId}`;
    return { instanceId: internalInstanceId, nodeId: vizNode };
  }, [instanceId, nodeId, node, vizNode]);

  const getFreqBins = useCallback((endpointId: string) => {
    if (!resolvedPath) return null;
    return getFrequencyBins(resolvedPath.instanceId, resolvedPath.nodeId, endpointId);
  }, [resolvedPath]);

  const render = useCallback((dt?: number) => {
    if (!resolvedPath) return;
    const renderer = getVideoRenderer(resolvedPath.instanceId, resolvedPath.nodeId);
    if (!renderer) return; // No renderer yet (e.g. VideoCanvas with no source connected)
    renderer.render(dt || 0);
  }, [resolvedPath]);

  const setCanvas = useCallback((canvas: HTMLCanvasElement) => {
    if (!resolvedPath) return;
    logger.trace('NodeVisualization', `setting canvas at ${resolvedPath.instanceId}.${resolvedPath.nodeId}`);
    setVideoCanvas(resolvedPath.instanceId, resolvedPath.nodeId, canvas);
  }, [resolvedPath]);

  const setSize = useCallback((width: number, height: number) => {
    if (!resolvedPath) return;
    setVideoSize(resolvedPath.instanceId, resolvedPath.nodeId, width, height);
  }, [resolvedPath]);

  /**
   * Find exposed endpoint on parent definition that maps to an internal endpoint.
   * Returns { exposedName, parentInstanceId, parentNodeId } if found.
   */
  const findExposedEndpoint = useCallback((internalNodeId: string, endpointId: string): {
    exposedName: string;
    parentInstanceId: string;
    parentNodeId: string;
  } | null => {
    // Only relevant for subgraphs (vizNode case)
    if (!vizNode || !nodeType) return null;

    // Get the parent definition (the subgraph's definition, e.g., MeshViewer)
    const parentDef = definitionState[nodeType];
    if (!parentDef?.api) return null;

    // Look for an exposed endpoint with path matching "internalNodeId.endpointId"
    const targetPath = `${internalNodeId}.${endpointId}`;
    for (const [exposedName, exposed] of Object.entries(parentDef.api) as [string, ExposedEndpoint][]) {
      if (exposed.path === targetPath) {
        return {
          exposedName,
          parentInstanceId: instanceId,
          parentNodeId: nodeId
        };
      }
    }
    return null;
  }, [vizNode, nodeType, definitionState, instanceId, nodeId]);

  const setValue = useCallback((endpointId: string, value: unknown) => {
    if (!resolvedPath) return;

    // Check if this endpoint is exposed on the parent definition
    const exposed = findExposedEndpoint(resolvedPath.nodeId, endpointId);
    if (exposed) {
      // Route through the exposed endpoint for per-instance values
      tweakValue(exposed.parentInstanceId, exposed.parentNodeId, exposed.exposedName, value);
    } else {
      // Not exposed - update internal node directly (shared across instances)
      tweakValue(resolvedPath.instanceId, resolvedPath.nodeId, endpointId, value);
    }
  }, [resolvedPath, findExposedEndpoint, tweakValue]);

  const getVal = useCallback((endpointId: string) => {
    if (!resolvedPath) return undefined;
    return getValue(resolvedPath.instanceId, resolvedPath.nodeId, endpointId);
  }, [resolvedPath, getValue]);

  const getPay = useCallback((endpointId: string) => {
    if (!resolvedPath) return undefined;
    return getPayload(resolvedPath.instanceId, resolvedPath.nodeId, endpointId);
  }, [resolvedPath, getPayload]);

  // Return null if node doesn't exist
  if (!node) {
    logger.trace('NodeVisualization', `node ${nodeId} removed from ${defId}, skipping render`);
    return {
      render: () => { },
      setCanvas: () => { },
      setSize: () => { },
      setValue: () => { },
      getValue: () => null,
      getPayload: () => null,
      getFrequencyBins: () => null
    }
  }

  return {
    render,
    setCanvas,
    setSize,
    setValue,
    getValue: getVal,
    getPayload: getPay,
    getFrequencyBins: getFreqBins
  };
}